'use client'

import { CheckCircle2, Loader2 } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import {
  FlowClockIcon,
  FlowFilterIcon,
  FlowMailIcon,
  FlowTriggerIcon,
} from '@/components/marketing/icons/automation-flow-icons'

interface AutomationFlowAnimationProps {
  isHovered?: boolean
}

export function AutomationFlowAnimation({
  isHovered = false,
}: AutomationFlowAnimationProps) {
  const ref = useRef(null)
  const [elapsedMs, setElapsedMs] = useState(0)

  useEffect(() => {
    let animationFrameId = 0

    if (!isHovered) {
      animationFrameId = requestAnimationFrame(() => {
        setElapsedMs(0)
      })
      return () => cancelAnimationFrame(animationFrameId)
    }

    const startTime = performance.now()
    const tick = (currentTime: number) => {
      setElapsedMs(currentTime - startTime)
      animationFrameId = requestAnimationFrame(tick)
    }

    animationFrameId = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [isHovered])

  const shouldAnimate = isHovered && elapsedMs >= 1500
  const isToolCallVisible = isHovered && elapsedMs >= 1500
  const isFlowVisible = isHovered && elapsedMs >= 3500
  const isFlowGenerated = isHovered && elapsedMs >= 3500
  const isSecondMessageVisible = isHovered && elapsedMs >= 8000
  const isSecondResponseVisible = isHovered && elapsedMs >= 9500
  const isUpdateComplete = isHovered && elapsedMs >= 11_500

  return (
    <div
      ref={ref}
      className="flex h-full w-full items-center justify-center px-6 pt-6"
    >
      <motion.div
        className="flex h-full w-full max-w-5xl items-start gap-20"
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 20,
        }}
      >
        {/* Left Side - Chat */}
        <div className="flex flex-1 flex-col gap-6 pt-4">
          {/* User Message - Right Aligned */}
          <div className="flex items-end justify-end gap-2">
            <motion.div
              className="max-w-[280px] rounded-xl rounded-br-sm bg-white px-3 py-2 text-zinc-900 shadow-sm dark:bg-white"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.8,
                ease: 'easeOut',
                delay: 0.5,
              }}
            >
              <p className="text-sm leading-relaxed">
                Create an onboarding flow for new signups
              </p>
            </motion.div>
            <motion.div
              className="flex w-fit shrink-0 items-center rounded-full border border-zinc-200 bg-white dark:border-zinc-700"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.5,
                ease: 'easeOut',
                delay: 0.5,
              }}
            >
              <Image
                src="https://randomuser.me/api/portraits/women/79.jpg"
                alt="User Avatar"
                className="size-8 shrink-0 rounded-full"
                width={32}
                height={32}
                sizes="32px"
                quality={60}
              />
            </motion.div>
          </div>

          {/* Brew Response - Left Aligned */}
          <div className="flex items-end gap-2">
            {shouldAnimate ? (
              <motion.div
                className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.5,
                  ease: 'easeOut',
                }}
              >
                <Image
                  src="/images/circle_paper.png"
                  alt="Brew Logo"
                  width={32}
                  height={32}
                  className="size-8"
                />
              </motion.div>
            ) : null}

            <div className="max-w-[220px] flex-1">
              <AnimatePresence mode="wait">
                {shouldAnimate && isToolCallVisible ? (
                  <motion.div
                    key="tool-call"
                    className={`${
                      isFlowGenerated
                        ? 'border-green-200 bg-green-50'
                        : 'border-white/60 bg-white/80 backdrop-blur-sm'
                    } flex min-h-[48px] items-center gap-2 rounded-xl rounded-bl-sm border px-3 py-2 shadow-sm transition-colors duration-500`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.8,
                      ease: 'easeOut',
                    }}
                  >
                    <motion.div
                      className="relative flex h-4 w-4 shrink-0 items-center justify-center"
                      animate={isFlowGenerated ? { scale: [1, 1.2, 1] } : {}}
                      transition={{ duration: 0.5 }}
                    >
                      {isFlowGenerated ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      )}
                    </motion.div>
                    <span
                      className={`${
                        isFlowGenerated
                          ? 'font-medium text-green-700'
                          : 'text-zinc-700'
                      } flex-1 text-sm transition-colors duration-500`}
                    >
                      {isFlowGenerated
                        ? 'Automation generated'
                        : 'Generating...'}
                    </span>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </div>

          {/* Second User Message */}
          {isSecondMessageVisible ? (
            <div className="flex items-end justify-end gap-2">
              <motion.div
                className="max-w-[280px] rounded-xl rounded-br-sm bg-white px-3 py-2 text-zinc-900 shadow-sm dark:bg-white"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.8,
                  ease: 'easeOut',
                }}
              >
                <p className="text-sm leading-relaxed">
                  Update the setup flow to highlight our advanced filters
                </p>
              </motion.div>
              <motion.div
                className="flex w-fit shrink-0 items-center rounded-full border border-zinc-200 bg-white dark:border-zinc-700"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.5,
                  ease: 'easeOut',
                }}
              >
                <Image
                  src="https://randomuser.me/api/portraits/women/79.jpg"
                  alt="User Avatar"
                  className="size-8 shrink-0 rounded-full"
                  width={32}
                  height={32}
                  sizes="32px"
                  quality={60}
                />
              </motion.div>
            </div>
          ) : null}

          {/* Second Brew Response */}
          {isSecondResponseVisible ? (
            <div className="flex items-end gap-2">
              <motion.div
                className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.5,
                  ease: 'easeOut',
                }}
              >
                <Image
                  src="/images/circle_paper.png"
                  alt="Brew Logo"
                  width={32}
                  height={32}
                  className="size-8"
                />
              </motion.div>

              <div className="max-w-[220px] flex-1">
                <motion.div
                  className={`${
                    isUpdateComplete
                      ? 'border-green-200 bg-green-50'
                      : 'border-white/60 bg-white/80 backdrop-blur-sm'
                  } flex min-h-[48px] items-center gap-2 rounded-xl rounded-bl-sm border px-3 py-2 shadow-sm transition-colors duration-500`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.8,
                    ease: 'easeOut',
                  }}
                >
                  <motion.div
                    className="relative flex h-4 w-4 shrink-0 items-center justify-center"
                    animate={isUpdateComplete ? { scale: [1, 1.2, 1] } : {}}
                    transition={{ duration: 0.5 }}
                  >
                    {isUpdateComplete ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    )}
                  </motion.div>
                  <span
                    className={`${
                      isUpdateComplete
                        ? 'font-medium text-green-700'
                        : 'text-zinc-700'
                    } flex-1 text-sm transition-colors duration-500`}
                  >
                    {isUpdateComplete ? 'Automation updated' : 'Updating...'}
                  </span>
                </motion.div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Right Side - Flowchart Preview */}
        <div className="flex h-full flex-1 items-center justify-center">
          {shouldAnimate ? (
            <AnimatePresence mode="wait">
              {isFlowVisible ? (
                <motion.div
                  key="flow-preview"
                  className="w-full max-w-md"
                  initial={{ opacity: 0, scale: 0.98, y: 40 }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                    y: 0,
                    transition: {
                      duration: 1.5,
                      ease: [0.34, 1.56, 0.64, 1],
                    },
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.98,
                    y: 40,
                    transition: {
                      duration: 0.8,
                      ease: [0.32, 0, 0.67, 0],
                    },
                  }}
                >
                  {/* Flowchart */}
                  <div className="relative max-h-[520px] overflow-hidden rounded-xl border border-zinc-200 bg-white p-5 dark:border-zinc-700 dark:bg-zinc-900">
                    {/* Trigger Node */}
                    <motion.div
                      className="rounded-lg border border-zinc-300 bg-zinc-50 p-2.5 dark:border-zinc-600 dark:bg-zinc-800"
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-zinc-200 dark:bg-zinc-700">
                          <FlowTriggerIcon className="h-3 w-3 text-zinc-700 dark:text-zinc-300" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-[8px] text-zinc-500 uppercase tracking-wide dark:text-zinc-400">
                            Trigger
                          </p>
                          <p className="font-semibold text-[11px] text-zinc-900 dark:text-zinc-100">
                            User signs up
                          </p>
                        </div>
                      </div>
                    </motion.div>

                    {/* Connecting line */}
                    <motion.div
                      className="mx-auto h-8 w-0.5 bg-zinc-300 dark:bg-zinc-600"
                      initial={{ scaleY: 0 }}
                      animate={{ scaleY: 1 }}
                      transition={{ delay: 0.5, duration: 0.2 }}
                      style={{ transformOrigin: 'top' }}
                    />

                    {/* Send Email Node 1 */}
                    <motion.div
                      className="rounded-lg border border-zinc-300 bg-white p-2.5 shadow-sm dark:border-zinc-600 dark:bg-zinc-800"
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7, duration: 0.5 }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex h-5 w-5 items-center justify-center rounded bg-zinc-100 dark:bg-zinc-700">
                          <FlowMailIcon className="h-3 w-3 text-zinc-700 dark:text-zinc-300" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-[11px] text-zinc-900 dark:text-zinc-100">
                            Welcome Email
                          </p>
                          <p className="text-[8px] text-zinc-500 dark:text-zinc-400">
                            Sent immediately
                          </p>
                        </div>
                      </div>
                    </motion.div>

                    {/* Connecting line */}
                    <motion.div
                      className="mx-auto h-8 w-0.5 bg-zinc-300 dark:bg-zinc-600"
                      initial={{ scaleY: 0 }}
                      animate={{ scaleY: 1 }}
                      transition={{ delay: 0.9, duration: 0.2 }}
                      style={{ transformOrigin: 'top' }}
                    />

                    {/* Wait Node */}
                    <motion.div
                      className="rounded-lg border border-zinc-300 bg-zinc-50 p-2.5 dark:border-zinc-600 dark:bg-zinc-800"
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.1, duration: 0.5 }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex h-5 w-5 items-center justify-center rounded bg-zinc-100 dark:bg-zinc-700">
                          <FlowClockIcon className="h-3 w-3 text-zinc-700 dark:text-zinc-300" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-[11px] text-zinc-900 dark:text-zinc-100">
                            Wait 2 days
                          </p>
                        </div>
                      </div>
                    </motion.div>

                    {/* Connecting line */}
                    <motion.div
                      className="mx-auto h-8 w-0.5 bg-zinc-300 dark:bg-zinc-600"
                      initial={{ scaleY: 0 }}
                      animate={{ scaleY: 1 }}
                      transition={{ delay: 1.3, duration: 0.2 }}
                      style={{ transformOrigin: 'top' }}
                    />

                    {/* Filter/Branch Node */}
                    <motion.div
                      className="rounded-lg border border-zinc-300 bg-zinc-50 p-2.5 dark:border-zinc-600 dark:bg-zinc-800"
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.5, duration: 0.5 }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex h-5 w-5 items-center justify-center rounded bg-zinc-100 dark:bg-zinc-700">
                          <FlowFilterIcon className="h-3 w-3 text-zinc-700 dark:text-zinc-300" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-[11px] text-zinc-900 dark:text-zinc-100">
                            Filter
                          </p>
                          <motion.p
                            className="text-[8px] text-zinc-500 dark:text-zinc-400"
                            animate={
                              isUpdateComplete ? { scale: [1, 1.05, 1] } : {}
                            }
                            transition={{ duration: 0.5 }}
                          >
                            {isUpdateComplete
                              ? 'Completed setup?'
                              : 'Opened email?'}
                          </motion.p>
                        </div>
                      </div>
                      <div className="flex gap-1.5 font-medium text-[8px]">
                        <div className="flex-1 rounded bg-zinc-100 px-2 py-1 text-center text-zinc-700 dark:bg-zinc-700 dark:text-zinc-300">
                          Yes
                        </div>
                        <div className="flex-1 rounded bg-zinc-100 px-2 py-1 text-center text-zinc-700 dark:bg-zinc-700 dark:text-zinc-300">
                          No
                        </div>
                      </div>
                    </motion.div>

                    {/* Connecting lines split */}
                    <div className="flex justify-between px-12">
                      <motion.div
                        className="h-8 w-0.5 bg-zinc-300 dark:bg-zinc-600"
                        initial={{ scaleY: 0 }}
                        animate={{ scaleY: 1 }}
                        transition={{ delay: 1.7, duration: 0.2 }}
                        style={{ transformOrigin: 'top' }}
                      />
                      <motion.div
                        className="h-8 w-0.5 bg-zinc-300 dark:bg-zinc-600"
                        initial={{ scaleY: 0 }}
                        animate={{ scaleY: 1 }}
                        transition={{ delay: 1.7, duration: 0.2 }}
                        style={{ transformOrigin: 'top' }}
                      />
                    </div>

                    {/* Final Email nodes */}
                    <div className="flex gap-2">
                      <motion.div
                        className={`flex-1 rounded-lg border border-zinc-300 bg-white p-2 shadow-sm transition-[border-color,box-shadow] duration-very-slow dark:border-zinc-600 dark:bg-zinc-800 ${
                          isUpdateComplete
                            ? 'border-primary/50 ring-2 ring-primary/50'
                            : ''
                        }`}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 1.9, duration: 0.5 }}
                      >
                        <div className="flex items-center gap-1.5">
                          <div className="flex h-4 w-4 items-center justify-center rounded bg-zinc-100 dark:bg-zinc-700">
                            <FlowMailIcon className="h-2.5 w-2.5 text-zinc-700 dark:text-zinc-300" />
                          </div>
                          <div className="flex-1">
                            <motion.p
                              className="font-semibold text-[10px] text-zinc-900 dark:text-zinc-100"
                              animate={
                                isUpdateComplete ? { scale: [1, 1.05, 1] } : {}
                              }
                              transition={{ duration: 0.5, delay: 0.2 }}
                            >
                              {isUpdateComplete
                                ? 'Advanced Filters Guide'
                                : 'Getting Started'}
                            </motion.p>
                            <motion.p
                              className="text-[8px] text-zinc-500 dark:text-zinc-400"
                              animate={
                                isUpdateComplete ? { scale: [1, 1.05, 1] } : {}
                              }
                              transition={{ duration: 0.5, delay: 0.2 }}
                            >
                              {isUpdateComplete
                                ? 'Segmentation tips'
                                : 'Product tips'}
                            </motion.p>
                          </div>
                        </div>
                      </motion.div>
                      <motion.div
                        className="flex-1 rounded-lg border border-zinc-300 bg-white p-2 shadow-sm dark:border-zinc-600 dark:bg-zinc-800"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 1.9, duration: 0.5 }}
                      >
                        <div className="flex items-center gap-1.5">
                          <div className="flex h-4 w-4 items-center justify-center rounded bg-zinc-100 dark:bg-zinc-700">
                            <FlowMailIcon className="h-2.5 w-2.5 text-zinc-700 dark:text-zinc-300" />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-[10px] text-zinc-900 dark:text-zinc-100">
                              Re-engage
                            </p>
                            <p className="text-[8px] text-zinc-500 dark:text-zinc-400">
                              Setup reminder
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          ) : null}
        </div>
      </motion.div>
    </div>
  )
}
